import React, { Component, Fragment } from 'react'
import { observer } from 'mobx-react'
import { observable, makeObservable } from 'mobx'
import i18n from 'i18next'
import AnimateHeight from 'react-animate-height'

import SearchLayout from '../Layouts/Search'
import Panel from '../../UniformComponents/Panel'
import DistributeEmptyState from '../../UniformComponents/DistributeEmptyState'

import TemplateSearchStore from './TemplateSearchStore'
import DataTable from '../DataTable'
import Typography from '../../UniformComponents/Typography'
import Anchor from '../../UniformComponents/Anchor'
import Tooltip from '../../UniformComponents/Tooltip'
import Checkbox from '../form/Checkbox'
import Button from '../../UniformComponents/Button'
import Badge from '../../UniformComponents/Badge'
import { PageSwitchTab, tabOptions } from '../PageSwitchTab'
import ChooseIndustryModal from '../Modals/ChooseIndustryModal'
import { allowedTemplateIndustries } from '../SurveyWizardNew/constants'
import DebugControl from '../DebugControl'

@observer
class TemplateSearch extends Component {
    @observable showChooseIndustry = false

    constructor(props) {
        super(props)
        makeObservable(this)
        this.store = props.store
        this.templateSearchStore = new TemplateSearchStore(props.store, 'survey')

        this.templateSearchStore.getTemplates()
        this.templateIndustries = this.store.client.industries.filter(
            (i) => allowedTemplateIndustries.includes(i.code),
        )

        this.state = {
            showFilterPanel: (!!props?.hideFilterAccordion || window.innerWidth > 992),
            showGuideAccordion: !!props.showGuideAtInit,
        }

        this.setColumns()
    }

    componentDidMount() {
        const { store } = this.props
        store.breadcrumps = [
            {
                name: 'survey templates',
                url: '/survey-templates',
                title: 'Templates',
            },
        ]
        window.addEventListener('resize', this.checkOverflow)
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.checkOverflow)
    }

    // find height diff of main-menu on overflow
    checkOverflow = () => {
        const { innerWidth } = window
        if (innerWidth > 992) {
            this.setState({ showFilterPanel: true })
        } else this.setState({ showFilterPanel: !!this.props.hideFilterAccordion || false })
    }

    toggleFilterPanel = () => {
        if (window.innerWidth > 992) return

        this.setState((prevState) => ({
            showFilterPanel: !prevState.showFilterPanel,
        }))
    }

    toggleGuide = () => {
        this.setState((prevState) => ({
            showGuideAccordion: !prevState.showGuideAccordion,
        }))
    }

    renderPageTitle = (pageTitle, showFilterPanel, store) => (
        <div className="heading">
            <div
                className="heading"
                style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}
                onClick={(e) => {
                    this.toggleFilterPanel()
                }}
            >
                <h3>{pageTitle}</h3>

                <Fragment>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <i className="filter-icon fa-regular fa-bars-filter" />
                        <p className="filter-text">{i18n.t('report_filters')}</p>
                        <i className={`panel-toggle-icon fa-regular ${showFilterPanel ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
                    </div>
                </Fragment>
            </div>

            {pageTitle === 'Questions' && store && (
                <div style={{ minHeight: 20, width: '100%' }}>
                    <DebugControl store={store} />
                </div>
            )}
        </div>
    )

    renderGuideAccordion = (showGuideAccordion, renderGuide) => (
        <Fragment>
            <hr className="heading-divider" />
            <div className="guide-accordion">
                <div
                    className="header"
                    onClick={this.toggleGuide}
                >
                    <i className="fa-solid fa-circle-info" />
                    <p>{i18n.t('preview.infoTitle')}</p>
                    <i className={`fa-light ${showGuideAccordion ? 'fa-square-chevron-up' : 'fa-square-chevron-down'}`} />
                </div>
                <AnimateHeight
                    className="guide-content"
                    duration={250}
                    height={showGuideAccordion ? 'auto' : 0}
                >
                    <hr className="divider" />
                    {renderGuide()}
                </AnimateHeight>
            </div>
        </Fragment>
    )

    setColumns = () => {
        const { templateSearchStore } = this
        this.columns = [
            {
                name: i18n.t('templates.templateName'),
                key: 'surveyName',
                cellClass: 'bold',
            },
            {
                name: i18n.t('status'),
                key: 'published',
                cellClass: 'status',
                dataTransform: (value) => {
                    const option = templateSearchStore.publishStatusOptions.find((opt) => opt.value === !!value)
                    return (
                        <Badge
                            label={option.label}
                            type={option.value ? 'blue' : 'grey'}
                            cssClass="status-badge rounded"
                        />
                    )
                },
            },
            {
                name: i18n.t('templates.eventType'),
                key: 'productType',
                dataTransform: (value) => (
                    <Typography>{templateSearchStore.getEventType(value)}</Typography>
                ),
            },
            {
                name: i18n.t('templates.surveyType'),
                key: 'surveyType',
                dataTransform: (value) => (
                    <Typography>{templateSearchStore.getSurveyType(value)}</Typography>
                ),
            },
            {
                name: i18n.t('templates.respondentType'),
                key: 'respondentType',
                dataTransform: (value) => (
                    <Typography>{templateSearchStore.getRespondentType(value)}</Typography>
                ),
            },
            {
                name: '',
                key: 'id',
                cellClass: 'overflow',
                dataTransform: (value) => (
                    <div className="survey-btn-wrapper">
                        <Anchor
                            cssClass="icon-link-btn"
                            onClick={(e) => e.stopPropagation()}
                            href={`/survey/${value}/setup`}
                        >
                            <Tooltip
                                text={i18n.t('templates.edit')}
                                direction="left"
                            />
                            <i className="fa-light fa-edit" />
                        </Anchor>
                    </div>
                ),
            },
        ]
    }

    renderGuide = () => (
        <div dangerouslySetInnerHTML={{
            __html: i18n.t('templates.introText'),
        }}
        />
    )

    goToTemplateWizard = (industryCode) => {
        const { store } = this.props
        store.goTo('full', `/survey/0/${industryCode}?surveytype=T`)
    }

    openNewTemplateOrIndustrySelector = () => {
        if (this.templateIndustries.length === 1) {
            this.goToTemplateWizard(this.templateIndustries[0].code)
        } else this.showChooseIndustry = true
    }

    renderTablePanel = () => {
        const { templateSearchStore } = this
        return (
            <DataTable
                store={templateSearchStore}
                inMemory
                dataSet={templateSearchStore.filteredList || []}
                columns={this.columns}
                cssClass="search-table"
            />
        )
    }

    renderButtons = () => (
        process.env.NEW_TEMPLATING ? (
            <Button
                cssClass="add-button primary regular"
                onClick={this.openNewTemplateOrIndustrySelector}
                icon="fa-light fa-circle-plus"
                label={i18n.t('templates.new')}
            />
        ) : null
    )

    renderFilterOptions = () => {
        const { templateSearchStore } = this
        return (
            <Fragment>
                {templateSearchStore.publishStatusOptions.length
                    ? (
                        <fieldset className="filter">
                            <legend className="legend-title">
                                {i18n.t('status')}
                            </legend>
                            <div>
                                {templateSearchStore.publishStatusOptions.map((opt) => (
                                    <Checkbox
                                        onBlur={templateSearchStore.changePublishedStatusFilter}
                                        checked={templateSearchStore.publishStatusFilter.includes(opt.value)}
                                        name={opt.label}
                                        label={opt.label}
                                    />
                                ))}
                            </div>
                        </fieldset>
                    ) : null}

                {templateSearchStore.productTypeOptions.length
                    ? (
                        <fieldset className="filter">
                            <Typography type="b">
                                {i18n.t('templates.eventType')}
                            </Typography>

                            <br />

                            <br />

                            {templateSearchStore.productTypeOptions.map((opt) => (
                                <Checkbox
                                    name={opt.value}
                                    label={opt.label}
                                    onBlur={templateSearchStore.changeProductFilter}
                                    checked={templateSearchStore.productFilter.includes(opt.value)}
                                />
                            ))}
                        </fieldset>
                    )
                    : null}

                {templateSearchStore.surveyTypeOptions.length
                    ? (
                        <fieldset className="filter">
                            <Typography type="b">
                                {i18n.t('templates.surveyType')}
                            </Typography>

                            <br />

                            <br />

                            {templateSearchStore.surveyTypeOptions.map((opt) => (
                                <Checkbox
                                    name={opt.value}
                                    label={opt.label}
                                    onBlur={templateSearchStore.changeSurveyFilter}
                                    checked={templateSearchStore.surveyFilter.includes(opt.value)}
                                />
                            ))}
                        </fieldset>
                    )
                    : null}

                {templateSearchStore.respondentTypeOptions.length
                    ? (
                        <fieldset className="filter">
                            <Typography type="b">
                                {i18n.t('templates.respondentType')}
                            </Typography>

                            <br />

                            <br />

                            {templateSearchStore.respondentTypeOptions.map((opt) => (
                                <Checkbox
                                    name={opt.value}
                                    label={opt.label}
                                    onBlur={templateSearchStore.changeRespondentFilter}
                                    checked={templateSearchStore.respondentFilter.includes(opt.value)}
                                />
                            ))}
                        </fieldset>
                    )
                    : null}
            </Fragment>
        )
    }

    render() {
        const {
            templateSearchStore: {
                templateList,
            },
        } = this
        const { store, location, history } = this.props
        return (
            <div>
                <div>
                    <PageSwitchTab
                        appStore={store}
                        location={location}
                        history={history}
                        tabOptions={tabOptions.templates}
                    />
                </div>
                {!templateList.length
                    ? (
                        <Fragment>
                            <div className="search-container has-teal-nav">
                                <div className="left-container">
                                    <Panel cssClass="filter-panel overflow-visible no-min-height">
                                        {this.renderPageTitle(i18n.t('templates.title'), this.state.showFilterPanel, store)}
                                        {this.renderGuideAccordion(this.state.showGuideAccordion, this.renderGuide)}
                                        <AnimateHeight
                                            className="filter-wrapper"
                                            duration={250}
                                            height={this.state.showFilterPanel ? 'auto' : 0}
                                            contentClassName="filter-content"
                                        >
                                            {this.renderFilterOptions()}
                                        </AnimateHeight>
                                    </Panel>
                                </div>

                                <div className="right-container">
                                    <DistributeEmptyState
                                        title={i18n.t('templates.emptySurveyStateTitle')}
                                        subtext={i18n.t('templates.emptySurveyStateSubtext')}
                                        icon="fal fa-clipboard-list"
                                        ctaText={i18n.t('templates.emptySurveyStateCTA')}
                                        ctaIcon="fal fa-clipboard-list"
                                        onClick={this.openNewTemplateOrIndustrySelector}
                                    />
                                </div>
                            </div>
                        </Fragment>
                    )
                    : (
                        <SearchLayout
                            pageTitle={i18n.t('templates.title')}
                            renderFilterOptions={this.renderFilterOptions}
                            renderTablePanel={this.renderTablePanel}
                            renderGuide={this.renderGuide}
                            renderButtons={this.renderButtons}
                            searchStore={this.templateSearchStore}
                            hasTopNav
                        />
                    )}
                <ChooseIndustryModal
                    industryChooseOpen={this.showChooseIndustry}
                    openCreateItem={this.goToTemplateWizard}
                    toggleIndustryChoose={() => { this.showChooseIndustry = false }}
                    industries={this.templateIndustries}
                />
            </div>
        )
    }
}

export default TemplateSearch
